import { Configuration, SuppliersApi } from '@/api';
import useGetCustomerQuery from '@/api/queries/useGetRetailerQuery';
import Button from '@/components/Button';
import CustomToast from '@/components/CustomToast';
import SpinnerIcon from '@/components/icons/SpinnerIcon';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import SettingsHeader from '@/modules/settings/components/SettingsHeader';
import CustomerForm from '@/modules/supplier/components/CustomerForm';
import { CustomerAddOrUpdate } from '@/modules/supplier/models/Customer';
import { CustomerListPath, CustomerStoreListPath } from '@/modules/supplier/paths';
import { BuildingStorefrontIcon, UsersIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const CustomerEdit = () => {
  const navigate = useNavigate();

  const { currentUser } = useAuth();

  const { customerId } = useParams();

  const supplierId = currentUser()?.supplierId;

  const {
    isLoading,
    retailer: customer,
    invalidateQuery,
  } = useGetCustomerQuery(customerId, supplierId);

  const suppliersApi = new SuppliersApi(new Configuration(useAuth));

  const addCustomerToSupplier = async (customer: CustomerAddOrUpdate) => {
    if (!supplierId || !customer.id) return;

    await suppliersApi.addRetailersToSupplier(supplierId, [
      {
        id: customer.id,
        customerCode: customer.customerCode,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Customer updated successfully!" visible={t.visible} />
    ));
  };

  const removeCustomerFromSupplier = async (customer: CustomerAddOrUpdate) => {
    if (!supplierId || !customer.id) return;

    await suppliersApi.removeRetailersFromSupplier(supplierId, [
      {
        id: customer.id,
      },
    ]);

    toast.custom((t) => (
      <CustomToast id={t.id} message="Customer removed successfully!" visible={t.visible} />
    ));
  };

  const handleSubmit = async (data: CustomerAddOrUpdate) => {
    if (!data) return;

    const shouldAddCustomer = data.customerCode !== '';

    try {
      if (shouldAddCustomer) {
        await addCustomerToSupplier(data);
      } else {
        await removeCustomerFromSupplier(data);
      }
      await invalidateQuery?.();
      navigate(CustomerListPath);
    } catch (error) {
      console.error('Error while updating customer:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-20">
        <SpinnerIcon />
      </div>
    );
  }

  if (!customer) return null;

  return (
    <>
      <SettingsHeader title="Edit Customer" icon={<UsersIcon className="w-6 h-6" />}>
        <Button
          to={CustomerStoreListPath.withCustomerId(customer.id)}
          icon={<BuildingStorefrontIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
          variant="secondary"
        >
          Stores
        </Button>
      </SettingsHeader>

      <CustomerForm customer={customer} onSubmit={handleSubmit} />
    </>
  );
};

export default CustomerEdit;
